import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

export type NgInlineActionErrorProps = {
  children: ReactNode;
  message: string;
  trigger: number | null;
  milisecondsUntilFadeOut?: number;
};

// Shown briefly next to a control whose command was accepted but then failed on the
// caller's device — the button's own disabled state already covers "not possible right now".
// Not a generalization of NgTooltip: that component is click-triggered, this one reacts to an
// external event (`trigger` changing), so a fresh message shows even if two failures land in a row.
export function NgInlineActionError({
  children,
  message,
  trigger,
  milisecondsUntilFadeOut = 3000,
}: NgInlineActionErrorProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (trigger === null) {
      return;
    }

    setVisible(true);
    const hideTimeout = setTimeout(() => setVisible(false), milisecondsUntilFadeOut);
    return () => clearTimeout(hideTimeout);
  }, [trigger, milisecondsUntilFadeOut]);

  return (
    <span className="position-relative d-inline-block">
      {children}
      {visible ? (
        <span
          role="alert"
          className="position-absolute top-100 start-50 translate-middle-x mt-1 px-2 py-1 rounded bg-danger text-white text-nowrap small"
          style={{ zIndex: 1 }}
        >
          {message}
        </span>
      ) : null}
    </span>
  );
}
